import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { BatchCombobox } from "@/components/batches/BatchCombobox";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { ArrowLeft, Lock } from "lucide-react";
import { KidHeroBanner } from "@/components/kid/KidHeroBanner";
import { KidStatBadges } from "@/components/kid/KidStatBadges";
import { KidOverviewTab } from "@/components/kid/KidOverviewTab";
import { KidStatsTab } from "@/components/kid/KidStatsTab";
import { KidFitnessTab } from "@/components/kid/KidFitnessTab";
import { KidSessionsTab } from "@/components/kid/KidSessionsTab";
import { KidAssessmentTab } from "@/components/kid/KidAssessmentTab";
import { KidProgressTab } from "@/components/kid/KidProgressTab";
import { KidPerformanceTab } from "@/components/kid/KidPerformanceTab";
import { apiRequest } from "@/lib/queryClient";
import { useAssignKidToBatch } from "@/hooks/useAssignKidToBatch";
import { asArray } from "@/lib/utils";
import type { KidProfile } from "@/components/kid/types";

interface BatchOption {
  id: number;
  name: string;
}

const COMING_SOON_TABS = ["Records", "Videos", "Photos"] as const;

function ComingSoonStub({ label }: { label: string }) {
  return (
    <div className="flex flex-col items-center justify-center py-20 text-center text-gray-400">
      <Lock className="w-8 h-8 mb-3 text-gray-300" />
      <p className="font-medium text-gray-500">{label}</p>
      <p className="text-sm mt-1">Coming soon</p>
    </div>
  );
}

function SkeletonBanner() {
  return (
    <div className="rounded-2xl bg-gray-100 animate-pulse h-28 w-full" />
  );
}

export default function CoachKidDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const {
    data: kid,
    isLoading,
    isError,
  } = useQuery<KidProfile>({
    queryKey: ["player-sessions", id],
    queryFn: async () => {
      const res: any = await apiRequest("GET", `/api/players/${id}/sessions`);
      return res.data as KidProfile;
    },
    enabled: !!id,
    retry: false,
  });

  const { data: batchesData } = useQuery({
    queryKey: ["/api/batches"],
    queryFn: async () => apiRequest("GET", "/api/batches"),
  });
  const batches: BatchOption[] = asArray<BatchOption>(batchesData?.data);

  const assignKid = useAssignKidToBatch();
  const [pendingBatchId, setPendingBatchId] = useState<number | null>(null);

  function handleBatchChange(value: string) {
    if (!kid) return;
    const newBatchId = Number(value);
    if (kid.batchId) {
      // Already assigned somewhere — confirm before moving.
      setPendingBatchId(newBatchId);
    } else {
      assignKid.mutate({ batchId: newBatchId, kidId: kid.id });
    }
  }

  function confirmMove() {
    if (!kid || pendingBatchId === null) return;
    assignKid.mutate({ batchId: pendingBatchId, kidId: kid.id });
    setPendingBatchId(null);
  }

  if (isLoading) {
    return (
      <div
        className="px-4 py-6 md:px-6 md:py-8 max-w-4xl mx-auto space-y-5"
        data-testid="coach-kid-detail-page"
      >
        <div className="h-8 w-16 bg-gray-100 rounded animate-pulse" />
        <SkeletonBanner />
        <div className="grid grid-cols-3 gap-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-16 rounded-xl bg-gray-100 animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  if (isError || !kid) {
    return (
      <div
        className="px-4 py-12 text-center text-gray-500"
        data-testid="kid-detail-not-found"
      >
        <p className="font-medium">Player not found.</p>
        <Button
          variant="ghost"
          className="mt-4"
          onClick={() => navigate("/coach/teams")}
        >
          Back to Teams
        </Button>
      </div>
    );
  }

  return (
    <div
      className="px-4 py-6 md:px-6 md:py-8 max-w-4xl mx-auto space-y-5"
      data-testid="coach-kid-detail-page"
    >
      {/* Back nav */}
      <Button
        variant="ghost"
        size="sm"
        className="text-gray-500 -ml-2"
        onClick={() => navigate(-1)}
        data-testid="btn-back"
      >
        <ArrowLeft className="h-4 w-4 mr-1" />
        Back
      </Button>

      {/* Hero + stat badges */}
      <KidHeroBanner kid={kid} />
      <KidStatBadges kid={kid} />

      {/* Inline batch picker — current assignment or "Unassigned", editable
          right here so a coach who just approved a kid can place them
          immediately without leaving the page. */}
      <div
        className="bg-white rounded-xl border border-gray-200 p-4 flex items-center justify-between gap-3 flex-wrap"
        data-testid="kid-batch-picker"
      >
        <div>
          <p className="text-sm font-medium text-gray-900">Batch</p>
          <p className="text-xs text-gray-500" data-testid="kid-batch-current">
            {kid.batchId ? kid.batchName : "Unassigned"}
          </p>
        </div>
        <BatchCombobox
          batches={batches}
          value={kid.batchId ? String(kid.batchId) : undefined}
          onValueChange={handleBatchChange}
          placeholder="Assign to a batch"
          className="w-[220px]"
          testId="kid-batch-select"
        />
      </div>

      <AlertDialog open={pendingBatchId !== null} onOpenChange={(open) => !open && setPendingBatchId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Move player to a new batch?</AlertDialogTitle>
            <AlertDialogDescription>
              {kid.firstName} is currently in {kid.batchName}. Move to{" "}
              {batches.find((b) => b.id === pendingBatchId)?.name}?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setPendingBatchId(null)}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmMove}>Move</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Tabs */}
      <Tabs defaultValue="overview" className="w-full">
        <TabsList className="w-full justify-start flex-wrap gap-1 bg-white border border-gray-200 rounded-lg p-1 h-auto mb-1">
          <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="progress" data-testid="tab-progress">Progress</TabsTrigger>
          <TabsTrigger value="stats" data-testid="tab-stats">Stats</TabsTrigger>
          <TabsTrigger value="fitness" data-testid="tab-fitness">Fitness</TabsTrigger>
          <TabsTrigger value="assessment" data-testid="tab-assessment">Assessment</TabsTrigger>
          <TabsTrigger value="performance" data-testid="tab-performance">Performance</TabsTrigger>
          <TabsTrigger value="sessions" data-testid="tab-sessions">Sessions</TabsTrigger>
          {COMING_SOON_TABS.map((label) => (
            <TabsTrigger
              key={label}
              value={label.toLowerCase()}
              data-testid={`tab-${label.toLowerCase()}`}
              className="text-gray-400"
            >
              {label}
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="overview" data-testid="tab-content-overview">
          <KidOverviewTab
            data={{
              avgBattingScore: kid.avgBattingScore,
              avgBowlingScore: kid.avgBowlingScore,
              avgFieldingScore: kid.avgFieldingScore,
              totalSessions: kid.totalSessions,
              attendedSessions: kid.attendedSessions,
            }}
          />
        </TabsContent>

        <TabsContent value="progress" data-testid="tab-content-progress">
          <KidProgressTab kid={kid} />
        </TabsContent>

        <TabsContent value="stats" data-testid="tab-content-stats">
          <KidStatsTab
            data={{
              sessions: kid.sessions,
              avgBattingScore: kid.avgBattingScore,
              avgBowlingScore: kid.avgBowlingScore,
              avgFieldingScore: kid.avgFieldingScore,
            }}
          />
        </TabsContent>

        <TabsContent value="fitness" data-testid="tab-content-fitness">
          <KidFitnessTab data={{ fitnessRecords: kid.fitnessRecords }} />
        </TabsContent>

        <TabsContent value="assessment" data-testid="tab-content-assessment">
          <KidAssessmentTab kidId={kid.id} />
        </TabsContent>

        <TabsContent value="performance" data-testid="tab-content-performance">
          <KidPerformanceTab kidId={kid.id} />
        </TabsContent>

        <TabsContent value="sessions" data-testid="tab-content-sessions">
          <KidSessionsTab data={{ sessions: kid.sessions }} />
        </TabsContent>

        {COMING_SOON_TABS.map((label) => (
          <TabsContent key={label} value={label.toLowerCase()}>
            <ComingSoonStub label={label} />
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}
